import { create } from 'zustand'
import { offerService, type Offer, type CreateOfferDto, type UpdateOfferDto, type OfferFilters, type OfferStats } from '@/services/offerService'
import { authService } from '@/services/authService'

interface OfferStore {
  // State
  offers: Offer[]
  currentOffer: Offer | null
  loading: boolean
  error: string | null
  stats: OfferStats | null
  filters: OfferFilters
  
  // Actions
  setFilters: (filters: OfferFilters) => void
  clearFilters: () => void
  fetchOffers: () => Promise<void>
  fetchOfferById: (id: string) => Promise<void>
  createOffer: (data: CreateOfferDto) => Promise<void>
  updateOffer: (id: string, data: UpdateOfferDto) => Promise<void>
  deleteOffer: (id: string) => Promise<void>
  updateOfferStatus: (id: string, is_active: boolean) => Promise<void>
  fetchOfferStats: () => Promise<void>
  clearCurrentOffer: () => void
  clearError: () => void
}

const useOfferStore = create<OfferStore>((set, get) => ({
  // Initial state
  offers: [],
  currentOffer: null,
  loading: false,
  error: null,
  stats: null,
  filters: {
    page: 1,
    limit: 10,
    is_active: true
  },

  // Actions
  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters }
    }))
    get().fetchOffers()
  },

  clearFilters: () => {
    set({
      filters: {
        page: 1,
        limit: 10,
        is_active: true
      }
    })
    get().fetchOffers()
  },

  fetchOffers: async () => {
    console.log('[DEBUG] offerStore.fetchOffers called with filters:', get().filters)
    set({ loading: true, error: null })
    try {
      const response = await offerService.getOffers(get().filters)
      console.log('[DEBUG] offerService.getOffers response:', response)
      
      // Get current user info
      const isAdmin = authService.isAdmin()
      const merchantContext = authService.getMerchantContext()
      
      let filteredOffers = response.data || []
      console.log('[DEBUG] Initial filteredOffers count:', filteredOffers.length)
      
      // If user is not admin and has merchant context, filter offers by merchant_id
      if (!isAdmin && merchantContext?.merchantId) {
        const userMerchantId = merchantContext.merchantId
        filteredOffers = filteredOffers.filter((offer: Offer) =>
          offer.merchant_id === userMerchantId
        )
        console.log('[DEBUG] After merchant filter, filteredOffers count:', filteredOffers.length)
      }
      
      set({
        offers: filteredOffers,
        loading: false
      })
      console.log('[DEBUG] offerStore.set offers with count:', filteredOffers.length)
    } catch (error: any) {
      console.error('[DEBUG] offerStore.fetchOffers error:', error)
      set({
        error: error.response?.data?.error || 'Erreur lors du chargement des offres',
        loading: false
      })
    }
  },

  fetchOfferById: async (id: string) => {
    set({ loading: true, error: null })
    try {
      const response = await offerService.getOfferById(id)
      set({
        currentOffer: response.data,
        loading: false
      })
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors du chargement de l\'offre',
        loading: false
      })
    }
  },

  createOffer: async (data: CreateOfferDto) => {
    set({ loading: true, error: null })
    try {
      await offerService.createOffer(data)
      set({ loading: false })
      get().fetchOffers() // Refresh list
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors de la création de l\'offre',
        loading: false
      })
      throw error
    }
  },

  updateOffer: async (id: string, data: UpdateOfferDto) => {
    set({ loading: true, error: null })
    try {
      await offerService.updateOffer(id, data)
      set({ loading: false })
      get().fetchOffers() // Refresh list
      get().fetchOfferById(id) // Refresh current offer
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors de la mise à jour de l\'offre',
        loading: false
      })
      throw error
    }
  },

  deleteOffer: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await offerService.deleteOffer(id)
      set({ loading: false })
      get().fetchOffers() // Refresh list
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors de la suppression de l\'offre',
        loading: false
      })
      throw error
    }
  },

  updateOfferStatus: async (id: string, is_active: boolean) => {
    set({ loading: true, error: null })
    try {
      await offerService.updateOfferStatus(id, is_active)
      set({ loading: false })
      get().fetchOffers() // Refresh list
      get().fetchOfferById(id) // Refresh current offer
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors de la mise à jour du statut',
        loading: false
      })
      throw error
    }
  },

  fetchOfferStats: async () => {
    set({ loading: true, error: null })
    try {
      const stats = await offerService.getOfferStats()
      set({
        stats,
        loading: false
      })
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors du chargement des statistiques',
        loading: false
      })
    }
  },

  clearCurrentOffer: () => {
    set({ currentOffer: null })
  },

  clearError: () => {
    set({ error: null })
  }
}))

export default useOfferStore
